import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	getAndValidate,
	postAndValidate,
} from "~/shared/services/api/fetch-client.ts";

import { mfaPhoneNumberSchema } from "../schemas/mfa-schema.ts";

const emptyResponseSchema = z.unknown();

export const MFA_QUERY_KEY = ["mfaPhoneNumbers"];
export const CURRENT_USER_QUERY_KEY = ["currentUserInfo"];

export function useGetMfaPhoneNumbers() {
	return useQuery({
		queryKey: MFA_QUERY_KEY,
		queryFn: async () => {
			const result = await getAndValidate(
				apiRoutes.GETMFAPHONENUMBERBYUSER,
				z.array(mfaPhoneNumberSchema),
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
	});
}

export function useEnableMfa() {
	return useMutation({
		mutationFn: async () => {
			const result = await postAndValidate(
				apiRoutes.ENABLEMFAFORUSER,
				{},
				emptyResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
	});
}

export function useDisableMfa() {
	return useMutation({
		mutationFn: async () => {
			const result = await postAndValidate(
				apiRoutes.DISABLEMFAFORUSER,
				{},
				emptyResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
	});
}

export function useAddMfaPhoneNumber() {
	return useMutation({
		mutationFn: async (phoneNumber: string) => {
			const result = await postAndValidate(
				apiRoutes.ADDMFAPHONENUMBER,
				{ phoneNumber },
				emptyResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
	});
}

export function useRemoveMfaPhoneNumber() {
	return useMutation({
		mutationFn: async (phoneNumberId: string) => {
			const result = await postAndValidate(
				`${apiRoutes.REMOVESINGLEFAPHONENUMBER}?phoneNumberId=${encodeURIComponent(phoneNumberId)}`,
				{},
				emptyResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
	});
}

export function useInvalidateMfaQueries() {
	const queryClient = useQueryClient();
	return () => {
		void queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
		void queryClient.invalidateQueries({ queryKey: MFA_QUERY_KEY });
	};
}
